import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, Skel } from '../components/ui'
import { number, thaiDate } from '../lib/format'

interface ArchiveRow {
  id: string
  from_at: string
  to_at: string
  event_count: number
  first_seq: number
  last_seq: number
  chain_head: string
  object_key: string
  sha256: string
  created_at: string
  size_bytes: number
}

interface ArchiveRun {
  id: string
  object_key: string
  sha256: string
  event_count: number
  first_seq: number
  last_seq: number
  chain_head: string
  size_bytes: number
  verified: boolean
  verified_at: string
}

interface VerifyResult {
  archive_id: string
  valid: boolean
  digest_match: boolean
  chain_head_match: boolean
  sha256: string
  computed_sha256: string
  chain_head: string
  computed_chain_head: string
  event_count: number
  first_seq: number
  last_seq: number
  broken_seq?: number
  size_bytes: number
  verified_at: string
  problem?: string
}

type Check =
  | { state: 'busy' }
  | { state: 'done'; result: VerifyResult }
  | { state: 'error'; message: string }

const DAY = 24 * 60 * 60 * 1000

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

function inputDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function daysAgo(n: number): Date {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return d
}

function startOfDay(value: string): Date {
  return new Date(`${value}T00:00:00`)
}

// The date input is inclusive; the archive window is half-open [from, to), so the
// chosen end date becomes the next local midnight.
function endExclusive(value: string): Date {
  const d = startOfDay(value)
  d.setDate(d.getDate() + 1)
  return d
}

function inclusiveEnd(iso: string): string {
  return new Date(new Date(iso).getTime() - 1000).toISOString()
}

function bytesLabel(n: number): string {
  if (!n) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function shortHash(value: string, n = 12): string {
  return value.length > n ? `${value.slice(0, n)}…` : value
}

export function Archives() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['archives'], queryFn: () => api<ArchiveRow[]>('/v1/archives'), staleTime: 10_000 })

  const [from, setFrom] = useState(() => inputDate(daysAgo(6)))
  const [to, setTo] = useState(() => inputDate(new Date()))
  const [formError, setFormError] = useState('')
  const [last, setLast] = useState<ArchiveRun | null>(null)
  const [checks, setChecks] = useState<Record<string, Check>>({})

  const create = useMutation({
    mutationFn: (input: { from: string; to: string }) =>
      apiWrite<ArchiveRun>('/v1/archives', 'POST', input),
    onSuccess: (res) => {
      setLast(res)
      setFormError('')
      void qc.invalidateQueries({ queryKey: ['archives'] })
    },
    onError: (err) => {
      setFormError(err instanceof Error ? err.message : 'เก็บช่วงนี้ไม่สำเร็จ')
    },
  })

  function submit() {
    setFormError('')
    setLast(null)
    if (!from || !to) {
      setFormError('เลือกวันเริ่มและวันสิ้นสุด')
      return
    }
    const start = startOfDay(from)
    const end = endExclusive(to)
    if (!(end > start)) {
      setFormError('วันสิ้นสุดต้องไม่ก่อนวันเริ่ม')
      return
    }
    if (end.getTime() - start.getTime() > 90 * DAY) {
      setFormError('หนึ่งช่วงเก็บได้ไม่เกิน 90 วัน')
      return
    }
    create.mutate({ from: start.toISOString(), to: end.toISOString() })
  }

  async function verify(id: string) {
    setChecks((prev) => ({ ...prev, [id]: { state: 'busy' } }))
    try {
      const result = await api<VerifyResult>(`/v1/archives/${id}/verify`, { method: 'POST' })
      setChecks((prev) => ({ ...prev, [id]: { state: 'done', result } }))
      void qc.invalidateQueries({ queryKey: ['archives'] })
    } catch (err) {
      setChecks((prev) => ({
        ...prev,
        [id]: { state: 'error', message: err instanceof Error ? err.message : 'ตรวจสอบไม่สำเร็จ' },
      }))
    }
  }

  const rows = q.data ?? []
  const busy = create.isPending

  return (
    <>
      <PageHead
        title="คลังเก็บถาวร"
        sub="เก็บช่วงร่องรอยการตรวจสอบลง object storage พร้อม SHA-256 และพิสูจน์ hash chain ได้ทุกครั้งที่เรียกคืน"
      />

      <div className="grid-main">
        <div className="card card-pad">
          <div className="card-label">เก็บช่วงใหม่</div>
          <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.7, marginTop: 10 }}>
            เลือกช่วงวันที่ที่ต้องการปิดบัญชี ระบบจะเขียน NDJSON เรียงตามลำดับเหตุการณ์
            คำนวณ digest แล้วอ่านกลับมาตรวจสอบก่อนบันทึกว่าเก็บสำเร็จ
          </p>

          <div className="row" style={{ gap: 12, marginTop: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="field" style={{ maxWidth: 190 }}>
              <label htmlFor="archive-from">วันเริ่ม</label>
              <input id="archive-from" className="input" type="date" value={from}
                max={to} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="field" style={{ maxWidth: 190 }}>
              <label htmlFor="archive-to">วันสิ้นสุด (รวมวันนี้)</label>
              <input id="archive-to" className="input" type="date" value={to}
                min={from} onChange={(e) => setTo(e.target.value)} />
            </div>
            <button className="btn btn-primary" type="button" onClick={submit} disabled={busy}>
              {busy ? 'กำลังเก็บ…' : 'เก็บช่วงนี้'}
            </button>
          </div>

          {formError ? <div className="form-error" role="alert">{formError}</div> : null}

          <div className="dim" style={{ fontSize: 11.5, marginTop: 12 }}>
            เก็บได้ครั้งละไม่เกิน 90 วัน และช่วงที่ซ้ำจะเขียนทับรายการเดิมด้วยกุญแจเดียวกัน
            จึงรันซ้ำได้อย่างปลอดภัย
          </div>
        </div>

        <div className="card card-pad">
          <div className="card-label">การพิสูจน์</div>
          <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
            <li>ทุกบรรทัดเก็บ payload ตามไบต์ที่ถูกแฮชไว้ใน audit chain</li>
            <li>digest ครอบทั้ง manifest และไฟล์ NDJSON ไม่ใช่แค่ไฟล์เดียว</li>
            <li>ตรวจซ้ำหลังเขียน: อ่าน object กลับมาแล้วเทียบ sha256 และ chain head</li>
            <li>ปุ่มตรวจสอบจะคำนวณห่วงโซ่ใหม่ทั้งช่วงและเทียบกับค่าที่บันทึกไว้</li>
          </ul>
        </div>
      </div>

      {last ? (
        <div className="card card-pad" style={{ marginTop: 18, borderColor: last.verified ? 'rgba(46,125,100,0.4)' : 'rgba(166,58,74,0.45)' }}>
          <div className="row">
            <span className="card-label">ผลการเก็บล่าสุด</span>
            <span className={`chip${last.verified ? ' gold' : ''}`} style={{ marginLeft: 'auto' }}>
              {last.verified ? 'อ่านกลับและตรวจสอบผ่าน' : 'ยังไม่ผ่านการตรวจสอบ'}
            </span>
          </div>
          <div className="mono" style={{ fontSize: 12, marginTop: 10, wordBreak: 'break-all' }}>{last.object_key}</div>
          <div className="dim" style={{ fontSize: 12, marginTop: 8 }}>
            {number(last.event_count)} เหตุการณ์ · seq {number(last.first_seq)}–{number(last.last_seq)} ·{' '}
            {bytesLabel(last.size_bytes)} · sha256 {shortHash(last.sha256, 20)}
          </div>
        </div>
      ) : null}

      <div className="card-label" style={{ marginTop: 26, marginBottom: 10 }}>คลังที่เก็บแล้ว</div>

      {q.isLoading ? <div className="stack"><Skel h={44} /><Skel h={44} /><Skel h={44} /></div>
        : rows.length === 0 ? (
          <div className="card">
            <Empty title="ยังไม่มีคลังเก็บถาวร" body="เลือกช่วงวันที่ด้านบนเพื่อเก็บร่องรอยการตรวจสอบชุดแรก" />
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>ช่วงเวลา</th><th>เหตุการณ์</th><th>SHA-256</th><th>ขนาด</th>
                  <th>chain head</th><th>ตรวจสอบ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const check = checks[row.id]
                  return (
                    <tr key={row.id} style={{ cursor: 'default' }}>
                      <td>
                        <div>{thaiDate(row.from_at)} — {thaiDate(inclusiveEnd(row.to_at))}</div>
                        <div className="dim" style={{ fontSize: 10.5 }}>ไม่รวมปลายช่วง</div>
                      </td>
                      <td>
                        <div className="tnum">{number(row.event_count)}</div>
                        <div className="dim mono" style={{ fontSize: 10.5 }}>
                          #{number(row.first_seq)}–#{number(row.last_seq)}
                        </div>
                      </td>
                      <td>
                        <span className="mono" style={{ fontSize: 10.5 }} title={row.sha256}>
                          {shortHash(row.sha256)}
                        </span>
                      </td>
                      <td className="dim tnum" style={{ fontSize: 11.5 }}>{bytesLabel(row.size_bytes)}</td>
                      <td>
                        <span className="mono dim" style={{ fontSize: 10.5 }} title={row.chain_head}>
                          {shortHash(row.chain_head)}
                        </span>
                      </td>
                      <td style={{ minWidth: 190 }}>
                        <button className="btn btn-sm btn-ghost" type="button"
                          disabled={check?.state === 'busy'} onClick={() => void verify(row.id)}>
                          {check?.state === 'busy' ? 'กำลังตรวจ…' : 'ตรวจสอบ'}
                        </button>
                        {check?.state === 'done' ? (
                          <div style={{ marginTop: 6, fontSize: 11 }}>
                            <span style={{ color: check.result.chain_head_match ? 'var(--color-emerald-soft)' : '#E3A0A9' }}>
                              {check.result.chain_head_match ? 'chain_head ตรงกัน' : 'chain_head ไม่ตรง'}
                            </span>
                            <span className="dim"> · digest {check.result.digest_match ? 'ตรง' : 'ไม่ตรง'}</span>
                            {!check.result.valid && check.result.problem ? (
                              <div className="dim" style={{ fontSize: 10.5, marginTop: 2 }}>{check.result.problem}</div>
                            ) : null}
                          </div>
                        ) : null}
                        {check?.state === 'error' ? (
                          <div style={{ marginTop: 6, fontSize: 10.5, color: '#E3A0A9' }}>{check.message}</div>
                        ) : null}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
    </>
  )
}
